import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import styled, { css, keyframes } from "styled-components";

import { Common } from "@src/styles/Common";

import {
    DeepStreamProgress,
    useDeepSummaryStream,
    useGetDeepSummaryAuctionAI,
    useGetDeepSummaryStatus,
    usePostDeepSummaryStart,
} from "../../api";
import { ReportItem } from "../MissgoAIGuide/optionUtils/DataTable/lib/DataTable.type";
import MarkdownParsing from "../MissgoAIGuide/optionUtils/MarkdownParsing";

const PHASE_STEPS = [
    { key: "preparing", label: "서류 준비 중", desc: "" },
    { key: "thinking", label: "경매 분석 중", desc: "경매 이력 및 입찰 현황을 분석하고 있습니다." },
    { key: "web_search", label: "시세 검색 중", desc: "주변 시세 및 실거래가를 조회하고 있습니다." },
    { key: "writing", label: "보고서 작성 중", desc: "분석 결과를 보고서로 정리하고 있습니다." },
    { key: "parsing", label: "결과 정리 중", desc: "최종 결과를 정리하고 있습니다." },
];

const STEP_PROGRESS = [0, 15, 35, 55, 80, 100];

const MissgoAIGuideDeep = () => {
    const searchParams = new URLSearchParams(location.search);
    const queryClient = useQueryClient();
    const itemId = searchParams.get("itemId") ?? "";

    const { data: deepSummaryData, isLoading: isSummaryLoading } = useGetDeepSummaryAuctionAI(itemId);
    const { data: statusData, isLoading: isStatusLoading } = useGetDeepSummaryStatus(itemId);
    const { mutate: postStart } = usePostDeepSummaryStart();
    const { connect, disconnect } = useDeepSummaryStream();

    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [currentMessage, setCurrentMessage] = useState("");
    const [progress, setProgress] = useState(0);
    const [displayedData, setDisplayedData] = useState<ReportItem[]>([]);

    const prevPhaseRef = useRef("");
    const autoConnectedRef = useRef(false);
    const isAnalyzingRef = useRef(false);

    const summaryData: ReportItem[] | null = deepSummaryData?.summary?.data ?? null;
    const deepStatus: string | undefined = statusData?.status;

    useEffect(() => {
        if (summaryData) {
            setDisplayedData(summaryData);
            isAnalyzingRef.current = false;
            setIsAnalyzing(false);
        }
    }, [summaryData]);

    useEffect(() => {
        return () => disconnect();
    }, []);

    // 프로그레스 바 애니메이션
    useEffect(() => {
        const target = STEP_PROGRESS[currentStepIndex + 1] ?? STEP_PROGRESS[currentStepIndex];
        let rafId = 0;
        let cancelled = false;
        const startVal = progress;
        const startTime = Date.now();
        const duration = 2500;

        const animate = () => {
            if (cancelled) return;
            const elapsed = Date.now() - startTime;
            const t = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setProgress(startVal + (target - startVal) * eased);
            if (t < 1) rafId = requestAnimationFrame(animate);
        };
        rafId = requestAnimationFrame(animate);

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
        };
    }, [currentStepIndex]);

    const connectStream = useCallback(
        (targetItemId: string) => {
            isAnalyzingRef.current = true;
            autoConnectedRef.current = true;
            setIsAnalyzing(true);
            connect(targetItemId, {
                onProgress: (data: DeepStreamProgress) => {
                    if (data.phase) {
                        const idx = PHASE_STEPS.findIndex((s) => s.key === data.phase);
                        if (idx >= 0 && data.phase !== prevPhaseRef.current) {
                            setCurrentStepIndex(idx);
                            prevPhaseRef.current = data.phase;
                        }
                    }
                    if (data.message) setCurrentMessage(data.message);
                },
                onDone: () => {
                    setCurrentStepIndex(PHASE_STEPS.length - 1);
                    setProgress(100);
                    queryClient.invalidateQueries(["deepSummaryAuctionAI", targetItemId]);
                },
                onError: () => {
                    isAnalyzingRef.current = false;
                    setIsAnalyzing(false);
                    queryClient.invalidateQueries(["deepSummaryAuctionAI", targetItemId]);
                },
            });
        },
        [connect, queryClient],
    );

    // status가 running이면 자동 SSE 연결
    useEffect(() => {
        if (isStatusLoading || isAnalyzingRef.current || autoConnectedRef.current || !itemId) return;
        if (deepStatus === "running") {
            connectStream(itemId);
        }
    }, [deepStatus, isStatusLoading, itemId, connectStream]);

    const handleAnalyze = () => {
        if (isAnalyzingRef.current) return;

        isAnalyzingRef.current = true;
        autoConnectedRef.current = true;
        setCurrentStepIndex(0);
        setCurrentMessage("");
        setProgress(0);
        prevPhaseRef.current = "";
        setIsAnalyzing(true);

        postStart(itemId, {
            onSuccess: (data) => {
                if (data?.status === "done") {
                    queryClient.invalidateQueries(["deepSummaryAuctionAI", itemId]);
                } else {
                    connectStream(itemId);
                }
            },
            onError: () => {
                isAnalyzingRef.current = false;
                autoConnectedRef.current = false;
                setIsAnalyzing(false);
            },
        });
    };

    // 최초 로딩
    if (isSummaryLoading || isStatusLoading) {
        return (
            <LoadingScreen>
                <Spinner />
                <LoadingTitle>데이터를 불러오는 중...</LoadingTitle>
            </LoadingScreen>
        );
    }

    // 분석 진행 중
    if (isAnalyzing) {
        return (
            <LoadingScreen>
                <PulseIconWrapper>
                    <RotatingSvg>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                            <circle cx="16" cy="16" r="12" stroke="#2275ee" strokeWidth="2" strokeDasharray="20 60" strokeLinecap="round" />
                            <circle cx="16" cy="16" r="6" stroke="#5a9bff" strokeWidth="1.5" strokeDasharray="10 30" strokeLinecap="round" />
                        </svg>
                    </RotatingSvg>
                </PulseIconWrapper>

                <AnalyzingTitle>AI가 분석 중입니다...</AnalyzingTitle>
                <AnalyzingSubtitle>잠시만 기다려 주세요. 약 4~5분 소요됩니다.</AnalyzingSubtitle>

                <ProgressSection>
                    <ProgressPercent>{Math.round(progress)}%</ProgressPercent>
                    <ProgressTrack>
                        <ProgressFill style={{ width: `${progress}%` }}>
                            <ShimmerOverlay />
                        </ProgressFill>
                    </ProgressTrack>
                </ProgressSection>

                <StepList>
                    {PHASE_STEPS.map((step, i) => {
                        const isDone = i < currentStepIndex;
                        const isActive = i === currentStepIndex;
                        const displayMsg = isActive && currentMessage ? currentMessage : step.label;

                        return (
                            <StepRow key={step.key} $isActive={isActive} $index={i}>
                                <StepCircleWrapper>
                                    {isDone ? (
                                        <DoneCircle>
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </DoneCircle>
                                    ) : isActive ? (
                                        <ActiveCircleWrapper>
                                            <ActivePulseRing />
                                            <ActiveRing />
                                            <ActiveDot />
                                        </ActiveCircleWrapper>
                                    ) : (
                                        <PendingCircle />
                                    )}
                                </StepCircleWrapper>
                                <StepContent>
                                    <StepLabel $isDone={isDone} $isActive={isActive}>
                                        {displayMsg}
                                    </StepLabel>
                                    {isActive && step.desc && <StepDesc>{step.desc}</StepDesc>}
                                </StepContent>
                            </StepRow>
                        );
                    })}
                </StepList>
            </LoadingScreen>
        );
    }

    // 분석 결과 없음
    if (!summaryData) {
        return (
            <NoDataScreen>
                <NoDataIconSvg />
                <NoDataTitle>분석 결과가 없습니다</NoDataTitle>
                <NoDataDescription>
                    AI가 이 물건의 핵심 정보를 분석해 드립니다.
                    <br />
                    아래 버튼을 눌러 분석을 요청해 보세요.
                </NoDataDescription>
                <InfoBox>
                    <InfoIcon>&#9432;</InfoIcon>
                    <InfoText>
                        분석에는 약 <InfoHighlight>4~5분</InfoHighlight>이 소요됩니다.
                        <br />
                        권리 분석, 가격 분석, 물건 상세 등 맞춤형
                        <br />
                        경매 리포트를 생성합니다.
                    </InfoText>
                </InfoBox>
                <AnalyzeButton onClick={handleAnalyze}>
                    <span>&#9733;</span>
                    분석 요청하기
                </AnalyzeButton>
            </NoDataScreen>
        );
    }

    // 결과 표시
    return (
        <ReportContainer>
            <WarningMessageBox>
                <div>※</div>
                <span>
                    본 정보는 <br />
                    미스고 데이터를 토대로 생성형 AI 서비스를 <br />
                    제공하고 있으므로 미스고의 동의 없이 무단수집은 <br />
                    민·형사상 처벌을 받을 수 있습니다.
                </span>
            </WarningMessageBox>
            <Line />
            <MarkdownParsing data={displayedData} />
        </ReportContainer>
    );
};

const NoDataIconSvg = () => (
    <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M32 10H68L88 30V98C88 101.314 85.314 104 82 104H32C28.686 104 26 101.314 26 98V16C26 12.686 28.686 10 32 10Z" fill="#E8EDF5" />
        <path d="M68 10L88 30H72C69.791 30 68 28.209 68 26V10Z" fill="#CDD5E0" />
        <rect x="38" y="42" rx="2" width="36" height="4" fill="#B0BAC9" />
        <rect x="38" y="54" rx="2" width="28" height="4" fill="#B0BAC9" />
        <rect x="38" y="66" rx="2" width="20" height="4" fill="#B0BAC9" />
        <rect x="38" y="78" rx="2" width="32" height="4" fill="#B0BAC9" />
        <circle cx="80" cy="88" r="18" fill="white" stroke="#2275EE" strokeWidth="3" />
        <circle cx="80" cy="88" r="8" fill="#2275EE" fillOpacity="0.15" />
        <line x1="93" y1="99" x2="100" y2="106" stroke="#2275EE" strokeWidth="3" strokeLinecap="round" />
    </svg>
);

// --- Keyframes ---
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const shimmer = keyframes`from { transform: translateX(-100%); } to { transform: translateX(200%); }`;
const pulseScale = keyframes`0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); }`;
const pulseRing = keyframes`0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.3); opacity: 0.3; }`;
const pulseDot = keyframes`0%, 100% { transform: scale(0.8); } 50% { transform: scale(1.1); }`;
const fadeSlideIn = keyframes`from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); }`;

// --- Styles ---
const LoadingScreen = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;
    gap: 6px;
`;

const Spinner = styled.div`
    width: 48px;
    height: 48px;
    border: 4px solid rgba(0, 0, 0, 0.08);
    border-top-color: #2275ee;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

const LoadingTitle = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: #0c002c;
    text-align: center;
    margin-top: 12px;
`;

const PulseIconWrapper = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: radial-gradient(circle at center, #eef4ff, #ddeaff);
    animation: ${pulseScale} 2s ease-in-out infinite;
`;

const RotatingSvg = styled.div`
    animation: ${spin} 3s linear infinite;
`;

const AnalyzingTitle = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: #0c002c;
    text-align: center;
`;

const AnalyzingSubtitle = styled.p`
    font-size: 12px;
    font-weight: 500;
    color: #888;
    text-align: center;
`;

const ProgressSection = styled.div`
    width: 100%;
    max-width: 350px;
    margin-top: 16px;
    margin-bottom: 10px;
`;

const ProgressPercent = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: #2275ee;
    margin-bottom: 8px;
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 8px;
    background: #eef0f5;
    border-radius: 9999px;
    overflow: hidden;
    position: relative;
`;

const ProgressFill = styled.div`
    height: 100%;
    border-radius: 9999px;
    background: linear-gradient(to right, #2275ee, #5a9bff);
    position: relative;
    overflow: hidden;
    transition: width 0.1s linear;
`;

const ShimmerOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: ${shimmer} 1.5s linear infinite;
`;

const StepList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    max-width: 350px;
`;

const StepRow = styled.div<{ $isActive: boolean; $index: number }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 15px;
    border-radius: 8px;
    background: ${({ $isActive }) => ($isActive ? "linear-gradient(170deg, #eef4ff 0%, #f5f8ff 100%)" : "transparent")};
    border: 1px solid ${({ $isActive }) => ($isActive ? "#d1ddf8" : "transparent")};
    transition: all 0.5s;
    animation: ${fadeSlideIn} 0.4s ease-out both;
    animation-delay: ${({ $index }) => $index * 0.1}s;
`;

const StepCircleWrapper = styled.div`
    flex-shrink: 0;
    width: 20px;
    height: 20px;
`;

const DoneCircle = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #2275ee;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ActiveCircleWrapper = styled.div`
    position: relative;
    width: 20px;
    height: 20px;
`;

const ActivePulseRing = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid #2275ee;
    animation: ${pulseRing} 1.5s infinite;
`;

const ActiveRing = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid #2275ee;
`;

const ActiveDot = styled.div`
    position: absolute;
    inset: 5px;
    border-radius: 50%;
    background: #2275ee;
    animation: ${pulseDot} 1.2s infinite;
`;

const PendingCircle = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid #dadada;
`;

const StepContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const StepLabel = styled.p<{ $isDone: boolean; $isActive: boolean }>`
    font-size: 14px;
    font-weight: ${({ $isActive }) => ($isActive ? 700 : 500)};
    color: ${({ $isDone, $isActive }) => ($isDone ? "#2275ee" : $isActive ? "#0c002c" : "#dadada")};
`;

const StepDesc = styled.p`
    font-size: 11px;
    font-weight: 500;
    color: #6688cc;
`;

const NoDataScreen = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 80px 35px 0;
    gap: 14px;
`;

const NoDataTitle = styled.p`
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
    color: #0c002c;
    text-align: center;
`;

const NoDataDescription = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    color: #888;
    text-align: center;
`;

const InfoBox = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    width: 320px;
    padding: 14px;
    border-radius: 10px;
    background: #f0f4ff;
    box-sizing: border-box;
`;

const InfoIcon = styled.div`
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    font-size: 18px;
    line-height: 18px;
    color: #2275ee;
`;

const InfoText = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    color: #444;
`;

const InfoHighlight = styled.span`
    color: #2275ee;
`;

const AnalyzeButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 320px;
    padding: 16px 32px;
    border-radius: 10px;
    background: #2275ee;
    box-shadow: 0px 4px 16px 0px rgba(34, 117, 238, 0.25);
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    line-height: 26px;
    cursor: pointer;
    box-sizing: border-box;

    span {
        font-size: 20px;
    }
`;

const ReportContainer = styled.div`
    max-width: 768px;
    height: 100%;
    margin: 0 auto;
    padding: 14px;
    padding-bottom: 30px;
    color: #333;
`;

const WarningMessageBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 24px;
    font-size: 12px;
    font-weight: 400;
    line-height: 140%;
    color: ${Common.colors.darkGrey04};
`;

const Line = styled.div`
    border: 1px solid #ddd;
    margin-bottom: 24px;
`;

export default MissgoAIGuideDeep;
